import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, Star, ChevronDown, ArrowRight, Loader, Plus, X, Upload, Check } from "lucide-react";
import SEO from "./SEO";
import { useTranslation } from "react-i18next";

const API_BASE = import.meta.env.VITE_API_URL || "https://daroulmesbackend.onrender.com";
const formatMAD = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0 MAD";
  return `${amount.toLocaleString("en-MA", { minimumFractionDigits: amount % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })} MAD`;
};


/* ─── Auth helpers ─── */
function getAuthToken() {
  try { return localStorage.getItem("dar_oulmes_token") || sessionStorage.getItem("dar_oulmes_token") || null; }
  catch { return null; }
}
function getAuthUser() {
  try {
    const raw = localStorage.getItem("dar_oulmes_user") || sessionStorage.getItem("dar_oulmes_user");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/* ─── Normalise a raw API product ─── */
function normaliseProduct(p) {
  return {
    id:      p.id,
    name:    p.name,
    desc:    p.description || "",
    price:   p.price,
    rating:  parseFloat((p.rating ?? 0).toFixed(1)),
    reviews: p._count?.reviews ?? p.reviews?.length ?? 0,
    tag:     p.tag || "",
    cat:     p.category?.name || "Uncategorized",
    catId:   p.categoryId || p.category?.id || null,
    img:     p.imageUrl
               ? p.imageUrl.startsWith("http")
                 ? p.imageUrl
                 : `${API_BASE}/api/images/uploads/${p.imageUrl.split("/").pop()}`
               : null,
  };
}

/* ─── Stars ─── */
function StarRow({ n = 0, reviews = 0 }) {
  const full = Math.floor(n);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={12}
          fill={i < full ? "#B5955D" : (i === full && (n - full) >= 0.5 ? "#B5955D" : "#e0d8cc")}
          color={i < full || (i === full && (n - full) >= 0.5) ? "#B5955D" : "#e0d8cc"}
          strokeWidth={1.5}
        />
      ))}
      {reviews > 0 && (
        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: "#9a8a78", marginLeft: 4 }}>
          {n.toFixed(1)} ({reviews})
        </span>
      )}
    </span>
  );
}

/* ─── Skeleton cards ─── */
function SkeletonRiad() {
  return (
    <div style={{ background: "#F4EFE6", borderRadius: 20, padding: 20, border: "1px solid rgba(200,160,101,.12)" }}>
      <div style={{ height: 190, borderRadius: 14, marginBottom: 14, background: "linear-gradient(90deg,#ede8de 25%,#f5f0e8 50%,#ede8de 75%)", backgroundSize: "200% 100%", animation: "pp-shimmer 1.4s infinite" }}/>
      <div style={{ height: 11, width: "55%", borderRadius: 4, background: "#ede8de", marginBottom: 8 }}/>
      <div style={{ height: 9,  width: "80%", borderRadius: 4, background: "#ede8de", marginBottom: 14 }}/>
      <div style={{ height: 36, borderRadius: 999, background: "#ede8de" }}/>
    </div>
  );
}
function SkeletonEss() {
  return (
    <div style={{ background: "#F4EFE6", borderRadius: 16, padding: 16, border: "1px solid rgba(200,160,101,.12)", display: "flex", gap: 14 }}>
      <div style={{ width: 90, height: 90, borderRadius: 12, flexShrink: 0, background: "linear-gradient(90deg,#ede8de 25%,#f5f0e8 50%,#ede8de 75%)", backgroundSize: "200% 100%", animation: "pp-shimmer 1.4s infinite" }}/>
      <div style={{ flex: 1 }}>
        <div style={{ height: 11, width: "60%", borderRadius: 4, background: "#ede8de", marginBottom: 8 }}/>
        <div style={{ height: 9,  width: "85%", borderRadius: 4, background: "#ede8de", marginBottom: 10 }}/>
        <div style={{ height: 9,  width: "40%", borderRadius: 4, background: "#ede8de" }}/>
      </div>
    </div>
  );
}

/* ─── Image with fallback ─── */
function ProductImage({ src, alt, style }) {
  const [err, setErr] = useState(false);
  if (!src || err) return (
    <div style={{ ...style, background: "linear-gradient(135deg,#e8dcc8,#d4c4a0)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ fontSize: 28, opacity: 0.35 }}>🏺</span>
    </div>
  );
  return <img src={src} alt={alt} style={style} onError={() => setErr(true)} />;
}

/* ─── Add Product Modal (Admin only) ─── */
function AddProductModal({ categories, token, onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm]         = useState({ name: "", description: "", price: "", categoryId: "", tag: "", rating: "" });
  const [imageFile, setImageFile] = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState(null);
  const [success,   setSuccess]   = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.price || !form.categoryId) {
      setErr(t("productsPage.required_error")); return;
    }
    setSaving(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("name",        form.name.trim());
      fd.append("description", form.description.trim());
      fd.append("price",       form.price);
      fd.append("categoryId",  form.categoryId);
      fd.append("tag",         form.tag.trim());
      fd.append("rating",      form.rating || "0");
      if (imageFile) fd.append("image", imageFile);

      const res  = await fetch(`${API_BASE}/api/products`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) { setErr(data.errors?.[0]?.msg || data.error || t("productsPage.failed_create")); setSaving(false); return; }
      setSuccess(true);
      setTimeout(() => { onCreated(data); onClose(); }, 800);
    } catch { setErr(t("productsPage.network_error")); setSaving(false); }
  };

  const inp = { width: "100%", padding: "9px 14px", border: "1.5px solid rgba(181,149,93,.35)", borderRadius: 10, fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: "#2A3E31", background: "#fdfaf5", outline: "none" };
  const lbl = { fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600, color: "#2A3E31", letterSpacing: ".04em", marginBottom: 5, display: "block" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(30,24,15,.55)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fdfaf5", borderRadius: 22, padding: 32, width: "100%", maxWidth: 520, border: "1px solid rgba(181,149,93,.25)", boxShadow: "0 24px 64px rgba(42,62,49,.18)", maxHeight: "90vh", overflowY: "auto" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 500, color: "#2A3E31", margin: 0 }}>{t("productsPage.add_new_product")}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#9a8a78" }}><X size={20}/></button>
        </div>

        {/* Image upload */}
        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>{t("productsPage.product_image")}</label>
          <div onClick={() => fileRef.current?.click()} style={{ height: 140, borderRadius: 12, border: "2px dashed rgba(181,149,93,.4)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", background: "#f8f3ea", transition: "border-color .2s" }}>
            {preview
              ? <img src={preview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }}/>
              : <div style={{ textAlign: "center", color: "#B5955D" }}>
                  <Upload size={28} style={{ marginBottom: 6 }}/>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#9a8a78" }}>{t("productsPage.upload_image")}</div>
                </div>
            }
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display: "none" }}/>
        </div>

        <div style={{ display: "grid", gap: 14 }}>
          <div><label style={lbl}>{t("productsPage.product_name")}</label><input style={inp} placeholder={t("productsPage.product_name_ph")} value={form.name} onChange={e => set("name", e.target.value)}/></div>
          <div><label style={lbl}>{t("productsPage.description")}</label><textarea style={{ ...inp, resize: "vertical", minHeight: 70 }} placeholder={t("productsPage.description_ph")} value={form.description} onChange={e => set("description", e.target.value)}/></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={lbl}>{t("productsPage.price_label")}</label><input style={inp} type="number" min="0" step="0.01" placeholder="0.00" value={form.price} onChange={e => set("price", e.target.value)}/></div>
            <div><label style={lbl}>{t("productsPage.rating_label")}</label><input style={inp} type="number" min="0" max="5" step="0.1" placeholder="4.5" value={form.rating} onChange={e => set("rating", e.target.value)}/></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>{t("productsPage.category_label")}</label>
              <select style={{ ...inp, appearance: "none" }} value={form.categoryId} onChange={e => set("categoryId", e.target.value)}>
                <option value="">{t("productsPage.select")}</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><label style={lbl}>{t("productsPage.tag")}</label><input style={inp} placeholder={t("productsPage.tag_ph")} value={form.tag} onChange={e => set("tag", e.target.value)}/></div>
          </div>
        </div>

        {err && <div style={{ marginTop: 14, padding: "10px 14px", background: "#fdf0f0", border: "1px solid #f5c6c6", borderRadius: 10, fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "#b04040" }}>{err}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px 0", borderRadius: 999, border: "1.5px solid rgba(181,149,93,.4)", background: "transparent", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: ".1em", color: "#9a8a78", cursor: "pointer" }}>
            {t("common.cancel")}
          </button>
          <button onClick={handleSubmit} disabled={saving || success} style={{ flex: 2, padding: "11px 0", borderRadius: 999, border: "none", background: success ? "#2d7a4a" : "#2A3E31", fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: ".12em", color: "#fff", cursor: saving ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background .3s" }}>
            {success ? <><Check size={13}/> {t("productsPage.created")}</> : saving ? <><Loader size={13} style={{ animation: "pp-spin 1s linear infinite" }}/> {t("productsPage.creating")}</> : t("productsPage.create_product")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════ MAIN ════ */
export default function ProductsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authUser = getAuthUser();
  const token    = getAuthToken();
  const isAdmin  = authUser?.role === "ADMIN";

  const [priceMin,    setPriceMin]   = useState(0);
  const [priceMax,    setPriceMax]   = useState(1000);
  const [collection,  setCollection] = useState("All");
  const [categories,  setCategories] = useState([]);
  const [riadProducts,setRiadProducts]= useState([]);
  const [essentials,  setEssentials] = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [error,       setError]      = useState(null);
  const [totalCount,  setTotalCount] = useState(0);
  const [showModal,   setShowModal]  = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/categories`).then(r => r.json()).then(data => setCategories(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true); setError(null);

    const params = new URLSearchParams({ limit: "50", includeMetadata: "true" });
    if (collection !== "All") params.set("category", collection);
    if (priceMax < 1000)      params.set("maxPrice", priceMax);
    if (priceMin > 0)         params.set("minPrice", priceMin);

    fetch(`${API_BASE}/api/products?${params}`, { signal: ctrl.signal })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        const raw = Array.isArray(data) ? data : (data.products ?? data.data ?? []);
        const all = raw.map(normaliseProduct);
        setTotalCount(all.length);
        setRiadProducts(all.slice(0, 2));
        setEssentials(all.slice(2));
        setLoading(false);
      })
      .catch(e => { if (e.name === "AbortError") return; setError("Could not load products."); setLoading(false); });

    return () => ctrl.abort();
  }, [collection, priceMin, priceMax]);

  const addToCart = (product) => {
    try {
      const cart = JSON.parse(localStorage.getItem("dar_oulmes_cart") || "[]");
      const ex = cart.find(i => i.id === product.id);
      if (ex) ex.quantity = (ex.quantity || 1) + 1;
      else cart.push({ id: product.id, name: product.name, price: product.price, img: product.img, tag: product.tag || "", quantity: 1 });
      localStorage.setItem("dar_oulmes_cart", JSON.stringify(cart));
      window.dispatchEvent(new Event("cart-updated"));
    } catch {}
  };

  const handleProductCreated = (raw) => {
    const norm = normaliseProduct(raw);
    setTotalCount(c => c + 1);
    if (riadProducts.length < 2) setRiadProducts(p => [...p, norm]);
    else setEssentials(e => [norm, ...e]);
  };

  const atcBtn = (item, small = false) => (
    <button
      onClick={(e) => { e.stopPropagation(); addToCart(item); }}
      style={{
        background: "#2A3E31", color: "#fff", border: "none",
        borderRadius: 999, padding: small ? "7px 14px" : "9px 18px",
        fontFamily: "'Montserrat',sans-serif", fontSize: small ? 9 : 10,
        fontWeight: 700, letterSpacing: ".14em", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 6,
        transition: "background .2s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#1a2e22"}
      onMouseLeave={e => e.currentTarget.style.background = "#2A3E31"}
    >
      <ShoppingCart size={small ? 11 : 12}/> {t("productsPage.add_to_cart")}
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", color: "#2A3E31", fontFamily: "'Cormorant Garamond',Georgia,serif", position: "relative", overflowX: "hidden", paddingTop: "100px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300&family=Montserrat:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .pp-deco { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }

        .pp-filter-bar {
          background: rgba(244,239,230,.88); backdrop-filter: blur(14px);
          border-radius: 20px; border: 1px solid rgba(255,255,255,.65);
          box-shadow: 0 4px 24px rgba(180,140,80,.08);
          padding: 20px 28px; display: flex; align-items: center; gap: 0; margin-bottom: 52px;
        }
        .pp-fdiv { width:1px; height:44px; background:rgba(200,160,101,.28); flex-shrink:0; }
        .pp-select {
          width:100%; background:transparent; border:1.5px solid #B5955D; border-radius:999px;
          padding:9px 18px; font-family:'Montserrat',sans-serif; font-size:12px; color:#2A3E31;
          cursor:pointer; outline:none; appearance:none;
        }

        /* Riad grid: 2 product cards + Add More */
        .pp-riad-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:20px; }
        .pp-riad-card {
          background:#F4EFE6; border-radius:20px; padding:20px;
          display:flex; flex-direction:column;
          border:1px solid rgba(200,160,101,.18);
          box-shadow:0 4px 18px rgba(180,140,100,.07);
          transition:box-shadow .3s,transform .3s;
        }
        .pp-riad-card:hover { box-shadow:0 14px 36px rgba(180,140,100,.17); transform:translateY(-3px); }

        .pp-add-more {
          border-radius:20px; padding:20px; min-height:320px;
          border:2px dashed rgba(181,149,93,.35);
          display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px;
          cursor:pointer; background:rgba(244,239,230,.4);
          transition:border-color .25s,background .25s;
        }
        .pp-add-more:hover { border-color:#B5955D; background:rgba(181,149,93,.07); }
        .pp-add-more-icon {
          width:52px; height:52px; border-radius:50%;
          border:1.5px solid #B5955D;
          display:flex; align-items:center; justify-content:center;
          color:#B5955D; transition:background .25s;
        }
        .pp-add-more:hover .pp-add-more-icon { background:rgba(181,149,93,.12); }

        /* Essentials: horizontal cards (3-col) */
        .pp-ess-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
        .pp-ess-card {
          background:#F4EFE6; border-radius:16px; padding:16px;
          display:flex; gap:14px; align-items:flex-start;
          border:1px solid rgba(200,160,101,.14);
          box-shadow:0 3px 12px rgba(180,140,100,.06);
          transition:box-shadow .3s,transform .3s;
        }
        .pp-ess-card:hover { box-shadow:0 10px 28px rgba(180,140,100,.15); transform:translateY(-2px); }
        .pp-ess-img { width:90px; height:90px; border-radius:12px; flex-shrink:0; overflow:hidden; }

        .pp-tag {
          display:inline-block; padding:2px 9px; border-radius:999px;
          font-family:'Montserrat',sans-serif; font-size:9px; font-weight:700;
          letter-spacing:.08em; background:rgba(181,149,93,.15); color:#B5955D; margin-bottom:6px;
        }

        .pp-banner {
          background:#2A3E31; border-radius:20px; padding:36px 44px;
          display:flex; align-items:center; justify-content:space-between; gap:32px;
          box-shadow:0 16px 48px rgba(30,60,40,.25); position:relative; overflow:hidden;
        }
        .pp-banner-btn {
          background:#B5955D; color:#fff; border:none; border-radius:999px; padding:13px 28px;
          font-family:'Montserrat',sans-serif; font-size:11px; font-weight:600; letter-spacing:.1em;
          cursor:pointer; white-space:nowrap; display:flex; align-items:center; gap:8px;
          transition:background .25s,transform .2s; flex-shrink:0;
        }
        .pp-banner-btn:hover { background:#c8a87a; transform:translateY(-1px); }

        .pp-empty { grid-column:1/-1; text-align:center; padding:48px 0; color:#9a8a78; font-family:'Montserrat',sans-serif; font-size:13px; }

        @keyframes pp-shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes pp-spin    { to{transform:rotate(360deg)} }
        @keyframes pp-fadeUp  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        .pp-fu { animation:pp-fadeUp .65s ease both; }

        input[type=range] { accent-color:#B5955D; cursor:pointer; width:100%; }

        @media (max-width:860px) {
          .pp-riad-grid { grid-template-columns:1fr 1fr !important; }
          .pp-ess-grid  { grid-template-columns:1fr 1fr !important; }
          .pp-filter-bar { flex-wrap:wrap; gap:16px; padding:18px 20px; }
          .pp-fdiv { display:none; }
          .pp-banner { flex-direction:column; text-align:center; padding:28px 24px; }
        }
        @media (max-width:540px) {
          .pp-riad-grid { grid-template-columns:1fr !important; }
          .pp-ess-grid  { grid-template-columns:1fr !important; }
        }
      `}</style>
        <SEO
          title={t("productsPage.seo_title")}
          description="Browse all Dar Oulmès products including jewellery, clothes, kitchen essentials, and makeup. Local products with reasonable prices."
          canonical="/products"
          image="/og-image.jpg"
        />
      {/* Decorative bg */}
      <div className="pp-deco">
        <svg width="100%" height="100%" viewBox="0 0 900 1800" preserveAspectRatio="xMinYMin slice">
          {[0,22,44,66,88,110,132,154,176].map((r,i)=>(
            <line key={`a${i}`} x1="0" y1="0" x2={500*Math.cos(r*Math.PI/180)} y2={500*Math.sin(r*Math.PI/180)} stroke="#B5955D" strokeWidth=".8" strokeOpacity=".2"/>
          ))}
          {[0,22,44,66,88,110,132,154,176].map((r,i)=>(
            <line key={`b${i}`} x1="900" y1="1800" x2={900-500*Math.cos(r*Math.PI/180)} y2={1800-500*Math.sin(r*Math.PI/180)} stroke="#B5955D" strokeWidth=".8" strokeOpacity=".2"/>
          ))}
          <path d="M0,900 C150,820 300,980 450,900 C600,820 750,980 900,900" fill="none" stroke="#B5955D" strokeWidth="1.2" strokeOpacity=".15"/>
          {[[820,160],[860,280],[50,1600],[100,1700],[870,420]].map(([cx,cy],i)=>(
            <circle key={i} cx={cx} cy={cy} r="3" fill="#B5955D" opacity=".28"/>
          ))}
        </svg>
      </div>

      <div style={{ position:"relative", zIndex:1, maxWidth:1000, margin:"0 auto", padding:"0 28px 80px" }}>

        {/* Title */}
        <div style={{ textAlign:"center", marginBottom:36 }} className="pp-fu">
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(52px,7vw,76px)", fontWeight:400, color:"#2A3E31", margin:0 }}>{t("productsPage.title")}</h2>
        </div>

        {/* Filter bar: Results | Collection | Price */}
        <div className="pp-filter-bar pp-fu">

          {/* Results */}
          <div style={{ flex:"1 1 26%", paddingRight:24 }}>
            <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:600, color:"#2A3E31", marginBottom:10, letterSpacing:".05em" }}>{t("productsPage.results")}</p>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#7a6a58", padding:"9px 18px", border:"1.5px solid rgba(181,149,93,.35)", borderRadius:999, display:"flex", alignItems:"center", gap:8 }}>
              {loading
                ? <><Loader size={12} style={{ animation:"pp-spin 1s linear infinite" }}/> {t("productsPage.loading")}</>
                : <><span style={{ color:"#B5955D", fontWeight:700 }}>{totalCount}</span>&nbsp;{t("productsPage.products_found")}</>
              }
            </div>
          </div>

          <div className="pp-fdiv"/>

          {/* Collection */}
          <div style={{ flex:"1 1 26%", padding:"0 24px" }}>
            <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:600, color:"#2A3E31", marginBottom:10, letterSpacing:".05em" }}>{t("productsPage.collection")}</p>
            <div style={{ position:"relative" }}>
              <select className="pp-select" value={collection} onChange={e=>setCollection(e.target.value)}>
                <option value="All">{t("productsPage.all_collection")}</option>
                {categories.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <ChevronDown size={13} color="#B5955D" style={{ position:"absolute", right:16, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}/>
            </div>
          </div>

          <div className="pp-fdiv"/>

          {/* Price dual range */}
          <div style={{ flex:"1 1 40%", paddingLeft:24 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
              <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:600, color:"#2A3E31", letterSpacing:".05em", margin:0 }}>{t("productsPage.price")}</p>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"#B5955D", fontWeight:700 }}>
                {formatMAD(priceMin)} – {priceMax >= 1000 ? t("productsPage.price_plus", { price: 1000 }) : formatMAD(priceMax)}
              </span>
            </div>
            <div style={{ position:"relative", height:20 }}>
              {/* Track */}
              <div style={{ position:"absolute", top:"50%", left:0, right:0, height:4, background:"#ddd", borderRadius:2, transform:"translateY(-50%)" }}>
                <div style={{ position:"absolute", height:"100%", borderRadius:2, left:`${(priceMin/1000)*100}%`, right:`${100-(priceMax/1000)*100}%`, background:"linear-gradient(90deg,#B5955D,#CBAE75)" }}/>
              </div>
              {/* Inputs (stacked, transparent) */}
              <input type="range" min={0} max={1000} step={10} value={priceMin} onChange={e=>{ const v=+e.target.value; if(v<priceMax) setPriceMin(v); }} style={{ position:"absolute", inset:0, opacity:0, height:"100%", zIndex:2 }}/>
              <input type="range" min={0} max={1000} step={10} value={priceMax} onChange={e=>{ const v=+e.target.value; if(v>priceMin) setPriceMax(v); }} style={{ position:"absolute", inset:0, opacity:0, height:"100%", zIndex:2 }}/>
              {/* Thumb dots */}
              <div style={{ position:"absolute", top:"50%", left:`calc(${(priceMin/1000)*100}% - 7px)`, width:14, height:14, borderRadius:"50%", background:"#B5955D", boxShadow:"0 2px 8px rgba(181,149,93,.5)", transform:"translateY(-50%)", pointerEvents:"none", zIndex:3 }}/>
              <div style={{ position:"absolute", top:"50%", left:`calc(${(priceMax/1000)*100}% - 7px)`, width:14, height:14, borderRadius:"50%", background:"#B5955D", boxShadow:"0 2px 8px rgba(181,149,93,.5)", transform:"translateY(-50%)", pointerEvents:"none", zIndex:3 }}/>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"#9a8a78", marginTop:6 }}>
              <span>{formatMAD(0)}</span><span>{t("productsPage.price_plus", { price: 1000 })}</span>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ textAlign:"center", padding:"28px 0", marginBottom:28, fontFamily:"'Montserrat',sans-serif", fontSize:13, color:"#b04040" }}>
            {error}
            <button onClick={()=>setCollection(c=>c)} style={{ marginLeft:12, background:"none", border:"1px solid #b04040", borderRadius:999, padding:"5px 14px", cursor:"pointer", fontSize:11, color:"#b04040" }}>{t("productsPage.retry")}</button>
          </div>
        )}

        {/* ── SECTION 1: The Modern Riad Collection ── */}
        <div style={{ marginBottom:60 }} className="pp-fu">
          <div style={{ textAlign:"right", marginBottom:16 }}>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:38, fontWeight:400, color:"#2A3E31", margin:0 }}>{t("productsPage.modern_riad")}</h3>
            <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:300, color:"#7a6a58", margin:"4px 0 0" }}>{t("productsPage.modern_riad_sub")}</p>
          </div>

          <div className="pp-riad-grid">
            {loading ? (
              <>{Array.from({ length: isAdmin ? 3 : 2 }, (_, i) => (
                <SkeletonRiad key={i} />
              ))}</>
            ) : (
              <>
                {riadProducts.length === 0 && !isAdmin && (
                  <div className="pp-empty">{t("productsPage.no_featured")}</div>
                )}

                {riadProducts.map(item => (
                  <div key={item.id} className="pp-riad-card" onClick={() => navigate(`/product/${item.id}`)} style={{ cursor: "pointer" }}>
                    {item.tag && <span className="pp-tag">{item.tag.toUpperCase()}</span>}
                    <div style={{ borderRadius:14, overflow:"hidden", marginBottom:14, height:190, background:"#ede8de" }}>
                      <ProductImage src={item.img} alt={item.name} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block", transition:"transform .5s" }}/>
                    </div>
                    <h4 style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, letterSpacing:".1em", color:"#2A3E31", marginBottom:5, lineHeight:1.4 }}>
                      {item.name.toUpperCase()}
                    </h4>
                    <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:300, color:"#7a6a58", lineHeight:1.65, flex:1, marginBottom:10 }}>
                      {item.desc || item.cat}
                    </p>
                    <StarRow n={item.rating} reviews={item.reviews}/>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:12 }}>
                      <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:26, fontWeight:700, color:"#2A3E31" }}>{formatMAD(item.price)}</span>
                      {atcBtn(item)}
                    </div>
                  </div>
                ))}

                {/* Admin: Add More slot */}
                {isAdmin && (
                  <div className="pp-add-more" onClick={()=>setShowModal(true)}>
                    <div className="pp-add-more-icon"><Plus size={22}/></div>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:600, letterSpacing:".08em", color:"#B5955D" }}>{t("productsPage.add_more")}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── SECTION 2: Elevated Daily Essentials ── */}
        <div style={{ marginBottom:60 }} className="pp-fu">
          <div style={{ textAlign:"right", marginBottom:20 }}>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:38, fontWeight:400, color:"#2A3E31", margin:0 }}>{t("productsPage.daily_essentials")}</h3>
          </div>

          <div className="pp-ess-grid">
            {loading
              ? Array.from({length:6}).map((_,i)=><SkeletonEss key={i}/>)
              : essentials.length === 0
                ? <div className="pp-empty">{t("productsPage.no_products_section")}</div>
                : essentials.map(item=>(
                    <div key={item.id} className="pp-ess-card" onClick={() => navigate(`/product/${item.id}`)} style={{ cursor: "pointer" }}>
                      <div className="pp-ess-img">
                        <ProductImage src={item.img} alt={item.name} style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}/>
                      </div>
                      <div style={{ flex:1, display:"flex", flexDirection:"column" }}>
                        {item.tag && <span className="pp-tag">{item.tag.toUpperCase()}</span>}
                        <h4 style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, letterSpacing:".1em", color:"#2A3E31", marginBottom:4, lineHeight:1.4 }}>
                          {item.name.toUpperCase()}
                        </h4>
                        <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:300, color:"#7a6a58", lineHeight:1.55, flex:1, marginBottom:8 }}>
                          {item.desc ? (item.desc.length > 65 ? item.desc.slice(0,65)+"…" : item.desc) : item.cat}
                        </p>
                        <StarRow n={item.rating} reviews={item.reviews}/>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8 }}>
                          <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:"#2A3E31" }}>{formatMAD(item.price)}</span>
                          {atcBtn(item, true)}
                        </div>
                      </div>
                    </div>
                  ))
            }
          </div>
        </div>

        {/* ── Bottom Banner ── */}
        <div className="pp-banner pp-fu">
          <svg style={{ position:"absolute", inset:0, opacity:.07, pointerEvents:"none" }} width="100%" height="100%" viewBox="0 0 300 200">
            {[0,60,120,180,240,300].map((x,i)=>[0,100,200].map((y,j)=>(
              <polygon key={`${i}${j}`} points={`${x},${y-20} ${x+10},${y} ${x},${y+20} ${x-10},${y}`} fill="#B5955D"/>
            )))}
          </svg>
          {/* Left: CTA button */}
          <button className="pp-banner-btn" style={{ position:"relative", zIndex:1 }}>
            <ArrowRight size={14}/> {t("productsPage.explore_rooms")}
          </button>
          {/* Center: text */}
          <div style={{ position:"relative", zIndex:1, textAlign:"right", flex:1 }}>
            <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:600, letterSpacing:".18em", color:"#B5955D", marginBottom:6 }}>{t("productsPage.customer_testimonial")}</p>
            <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:400, color:"#fff", marginBottom:8, lineHeight:1.2 }}>{t("productsPage.complete_room")}</h3>
            <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:300, color:"rgba(255,255,255,.65)", lineHeight:1.8, maxWidth:380, margin:"0 0 0 auto" }}>
              {t("productsPage.banner_sub")}
            </p>
          </div>
          {/* Right: icon */}
          <div style={{ width:64, height:80, border:"1.5px solid #B5955D", borderRadius:"32px 32px 8px 8px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, position:"relative", zIndex:1 }}>
            <span style={{ fontSize:28 }}>🏠</span>
          </div>
        </div>

      </div>

      {/* Admin modal */}
      {showModal && (
        <AddProductModal categories={categories} token={token} onClose={()=>setShowModal(false)} onCreated={handleProductCreated}/>
      )}
    </div>
  );
}